// @file Vue composable for all page related logic in Slideshow

import { trackEvent } from '@@/bits/analytics'
import { COVER_PAGE_INDEX } from '@@/bits/slideshow'
import { useSlideshowStore } from '@@/pinia/slideshow'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceMediaPlayerStore } from '@@/pinia/surface_media_player'
import type { Post } from '@@/types'
import type { Page } from '@@/types/slideshow'
import { PageType } from '@@/types/slideshow'
import { storeToRefs } from 'pinia'
import type { Ref } from 'vue'

export const useSlideshowPages = (): {
  pages: Ref<Page[]>
  currentPageIndex: Ref<number | null>
  currentPost: Ref<Post | null>
  currentPage: Ref<Page | null>
  currentPageNumber: Ref<number | null>
  currentPageUrl: Ref<string>
  coverPageUrl: Ref<string>
  lastPageIndex: Ref<number>
  prevPageIndex: Ref<number | null>
  nextPageIndex: Ref<number | null>
  isFirstPage: Ref<boolean>
  isLastPage: Ref<boolean>
  numberOfPages: Ref<number>

  getPageLastUpdated: (page: Page | null) => string
  goToPage: (pageIndex: number, keyboardTarget?: string) => void
  onPrevious: (keyboardTarget?: string) => void
  onNext: (keyboardTarget?: string) => void
  onRestart: () => void
} => {
  const slideshowStore = useSlideshowStore()
  const { wallUpdatedAt } = storeToRefs(useSurfaceStore())
  const surfaceMediaPlayerStore = useSurfaceMediaPlayerStore()

  const {
    pages,
    currentPageIndex,
    currentPost,
    currentPage,
    currentSection,
    currentPageNumber,
    currentPageUrl,
    coverPageUrl,
    lastPageIndex,
    prevPageIndex,
    nextPageIndex,
    isFirstPage,
    isLastPage,
    numberOfPages,
  } = storeToRefs(slideshowStore)

  const getPageLastUpdated = (page: Page | null): string => {
    if (page == null) return ''

    switch (page.pageType) {
      case PageType.Cover:
        // Although wallUpdatedAt doesn't always mean the cover page was updated, it's a good enough approximation
        // for the current use case which is to support slide exports.
        return wallUpdatedAt.value
      case PageType.Section:
        return currentSection.value?.updated_at ?? ''
      case PageType.Post:
        return currentPost.value?.updated_at?.toString() ?? ''
      default:
        return ''
    }
  }

  const goToPage = (pageIndex: number, keyboardTarget?: string): void => {
    if (pageIndex < COVER_PAGE_INDEX || pageIndex > lastPageIndex.value) {
      return
    }

    if (keyboardTarget != null) {
      trackEvent('keyboard', {
        target: keyboardTarget,
      })
    }

    currentPageIndex.value = pageIndex
    replaceUrl()

    // Always stop currently playing media when switching pages
    surfaceMediaPlayerStore.unsetCurrentlyPlayingMedia()
  }

  const onPrevious = (keyboardTarget?: string): void => {
    if (currentPageIndex.value == null) return
    goToPage(currentPageIndex.value - 1, keyboardTarget)
  }
  const onNext = (keyboardTarget?: string): void => {
    if (currentPageIndex.value == null) return
    goToPage(currentPageIndex.value + 1, keyboardTarget)
  }
  const onRestart = (): void => {
    goToPage(COVER_PAGE_INDEX)
  }

  const replaceUrl = (): void => {
    window.history.replaceState({}, '', currentPageUrl.value)
  }

  return {
    pages,
    currentPageIndex,
    currentPost,
    currentPage,
    currentPageNumber,
    getPageLastUpdated,
    lastPageIndex,
    prevPageIndex,
    nextPageIndex,
    isFirstPage,
    isLastPage,
    numberOfPages,
    currentPageUrl,
    coverPageUrl,

    goToPage,
    onPrevious,
    onNext,
    onRestart,
  }
}
